import React, { useState } from 'react';
import {
    fuelMapLevelUnitForFormTelemetry,
    fuelMapMappedUnitForFormTelemetry,
    isVoltageTelemetryInput,
} from '../../panel/inventory/fuelConfigurator/fuelPolicyApiMapping';
import '../mapBattery/mapBattery.css';

const FuelMapTable = ({ fuelMapRows = [], setFuelMapRows, disabled, telemetryInput = 'litre' }) => {
    const levelUnit = fuelMapLevelUnitForFormTelemetry(telemetryInput);
    const mappedUnit = fuelMapMappedUnitForFormTelemetry(telemetryInput);
    const isVoltageMode = isVoltageTelemetryInput(telemetryInput);
    const levelLabel = isVoltageMode ? 'Voltage' : 'Fuel level';
    const mappedLabel = isVoltageMode ? 'Litres' : 'Mapped value';
    const [editingIndex, setEditingIndex] = useState(null);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [fromVal, setFromVal] = useState('');
    const [toVal, setToVal] = useState('');
    const [mappedVal, setMappedVal] = useState('');

    const resetEditingState = () => {
        setEditingIndex(null);
        setIsAddingNew(false);
        setFromVal('');
        setToVal('');
        setMappedVal('');
    };

    const handleSave = (index) => {
        if (disabled) return;
        const next = [...fuelMapRows];
        next[index] = {
            ...next[index],
            fuelLevelFrom: parseFloat(fromVal) || 0,
            fuelLevelTo: parseFloat(toVal) || 0,
            mappedValue: parseFloat(mappedVal) || 0,
        };
        setFuelMapRows(next);
        resetEditingState();
    };

    const handleAddNew = () => {
        if (disabled) return;
        resetEditingState();
        setIsAddingNew(true);
    };

    const handleSaveNew = () => {
        if (disabled) return;
        const row = {
            id: fuelMapRows.length > 0 ? Math.max(...fuelMapRows.map((r) => r.id ?? 0)) + 1 : 1,
            fuelLevelFrom: parseFloat(fromVal) || 0,
            fuelLevelTo: parseFloat(toVal) || 0,
            mappedValue: parseFloat(mappedVal) || 0,
        };
        setFuelMapRows([...fuelMapRows, row]);
        resetEditingState();
    };

    const handleDelete = (index) => {
        if (disabled) return;
        setFuelMapRows(fuelMapRows.filter((_, i) => i !== index));
        if (editingIndex === index) resetEditingState();
    };

    const handleEdit = (index, map) => {
        if (disabled) return;
        resetEditingState();
        setEditingIndex(index);
        setFromVal(String(map.fuelLevelFrom));
        setToVal(String(map.fuelLevelTo));
        setMappedVal(String(map.mappedValue));
    };

    const rowKey = (map, index) => map.id ?? `${map.fuelLevelFrom}-${map.fuelLevelTo}-${index}`;

    return (
        <div className="voltage-map-table-container" style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
            <div className="voltage-map-table-header">
                <div>{`${levelLabel} from (${levelUnit})`}</div>
                <div>{`${levelLabel} to (${levelUnit})`}</div>
                <div>{`${mappedLabel} (${mappedUnit})`}</div>
                <div>Actions</div>
            </div>
            <div className="voltage-map-table-body-list-container">
                {fuelMapRows.map((map, index) => (
                    <div
                      key={rowKey(map, index)}
                      className={
                            index === fuelMapRows.length - 1 && !isAddingNew
                                ? 'voltage-map-table-body'
                                : 'voltage-map-table-body-with-divider'
                        }
                    >
                        {editingIndex === index ? (
                            <>
                                <input type="number" value={fromVal} onChange={(e) => setFromVal(e.target.value)} className="voltage-input" disabled={disabled} />
                                <input type="number" value={toVal} onChange={(e) => setToVal(e.target.value)} className="voltage-input" disabled={disabled} />
                                <input type="number" value={mappedVal} onChange={(e) => setMappedVal(e.target.value)} className="voltage-input" disabled={disabled} />
                                <div className="voltage-map-table-remove-button">
                                    <button type="button" onClick={() => handleSave(index)} disabled={disabled}>Save</button>
                                    <button type="button" onClick={resetEditingState} disabled={disabled}>Cancel</button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="voltage-map-table-data">{map.fuelLevelFrom}</div>
                                <div className="voltage-map-table-data">{map.fuelLevelTo}</div>
                                <div className="voltage-map-table-data">{map.mappedValue}{mappedUnit === '%' ? '%' : ''}</div>
                                <div className="voltage-map-table-remove-button">
                                    <button type="button" onClick={() => handleDelete(index)} disabled={disabled || isAddingNew || editingIndex !== null}>Delete</button>
                                    <button type="button" onClick={() => handleEdit(index, map)} disabled={disabled || isAddingNew || editingIndex !== null}>Edit</button>
                                </div>
                            </>
                        )}
                    </div>
                ))}
                {isAddingNew && (
                    <div className="voltage-map-table-body-with-divider">
                        <input type="number" value={fromVal} onChange={(e) => setFromVal(e.target.value)} placeholder="0" className="voltage-input" disabled={disabled} />
                        <input type="number" value={toVal} onChange={(e) => setToVal(e.target.value)} placeholder="0" className="voltage-input" disabled={disabled} />
                        <input type="number" value={mappedVal} onChange={(e) => setMappedVal(e.target.value)} placeholder="0" className="voltage-input" disabled={disabled} />
                        <div className="voltage-map-table-remove-button">
                            <button type="button" onClick={handleSaveNew} disabled={disabled}>Save</button>
                            <button type="button" onClick={resetEditingState} disabled={disabled}>Cancel</button>
                        </div>
                    </div>
                )}
            </div>
            <div
              className="voltage-map-table-button"
              onClick={handleAddNew}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddNew(); }}
              style={{
                    pointerEvents: disabled || editingIndex !== null ? 'none' : 'auto',
                    opacity: disabled || editingIndex !== null ? 0.5 : 1,
                }}
            >
                <div>Add map row</div>
            </div>
        </div>
    );
};

export default FuelMapTable;
